import React, { useState, useEffect } from 'react';
import { Form, message } from 'antd';
import ErrorIcon from '@/assets/icons/error.svg';
import { smsService, userService, authService } from '@/services/amai';
import { handleSmsError, getUserFriendlyMessage } from '@/utils/errorHandler';
import {
  StyledModal,
  ModalHeader,
  ModalTitle,
  CloseButton,
  LoginForm,
  PhoneInputContainer,
  PhonePrefix,
  PhoneDivider,
  PhoneInput,
  VerificationContainer,
  VerificationInput,
  SendCodeButton,
  AgreementText,
  AgreementLink,
  SubmitButton,
  ArrowIcon,
} from './PhoneLoginModal.styles';

interface PhoneLoginModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: (userInfo: any) => void;
}

interface LoginFormData {
  phone: string;
  code: string;
  countryCode: string;
}

const PhoneLoginModal: React.FC<PhoneLoginModalProps> = ({
  open,
  onCancel: _onCancel,
  onSuccess: _onSuccess,
}) => {
  // 添加调试信息
  console.log('[PhoneLoginModal] 组件渲染，open状态:', open);
  const [form] = Form.useForm<LoginFormData>();
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  // 监听表单字段变化
  const handleFormValuesChange = (_changedValues: any, allValues: LoginFormData) => {
    // 检查手机号是否有效
    if (allValues.phone) {
      const phoneRegex = /^1[3-9]\d{9}$/;
      if (phoneRegex.test(allValues.phone)) {
        setIsPhoneValid(true);
      } else {
        setIsPhoneValid(false);
      }
    } else {
      setIsPhoneValid(false);
    }

    // 检查登录按钮是否可以启用 - 需要手机号格式正确且验证码有值
    const phoneRegex = /^1[3-9]\d{9}$/;
    const isPhoneFormatValid = allValues.phone && phoneRegex.test(allValues.phone);
    const isCodeValid = allValues.code && /^\d{6}$/.test(allValues.code);

    if (isPhoneFormatValid && isCodeValid) {
      setIsSubmitDisabled(false);
    } else {
      setIsSubmitDisabled(true);
    }
  };

  // 倒计时效果
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // 键盘事件处理
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && open) {
        const activeElement = document.activeElement;
        if (activeElement?.getAttribute('placeholder') === '请输入验证码') {
          form.submit();
        }
      }
    };

    if (open) {
      document.addEventListener('keypress', handleKeyPress);
    }
    return () => document.removeEventListener('keypress', handleKeyPress);
  }, [form, open]);

  // 发送验证码
  const handleSendCode = async () => {
    try {
      const phone = form.getFieldValue('phone');

      if (!phone) {
        message.error('请输入手机号');
        return;
      }

      // 手机号格式验证
      const phoneRegex = /^1[3-9]\d{9}$/;
      if (!phoneRegex.test(phone)) {
        message.error('请输入正确的手机号格式');
        return;
      }

      setSendingCode(true);

      // 调用短信服务发送更新手机号验证码
      const response = await smsService.sendUpdatePhoneCode(phone, '86');

      if (response.code === 0) {
        message.success('验证码发送成功');
        setCountdown(60); // 开始60秒倒计时
      } else {
        // 使用统一的错误处理
        const errorInfo = handleSmsError(response, '发送验证码');
        message.error(getUserFriendlyMessage(errorInfo));
      }
    } catch (error: any) {
      console.error('发送验证码失败:', error);
      // 使用统一的短信错误处理
      const errorInfo = handleSmsError(error, '发送验证码');
      message.error(getUserFriendlyMessage(errorInfo));
    } finally {
      setSendingCode(false);
    }
  };

  // 手机号验证提交
  const handleSubmit = async (values: LoginFormData) => {
    try {
      setLoading(true);

      // 调用更新手机号接口
      const response = await userService.updatePhone({
        mobile: values.phone,
        country_code: '86',
        sms_code: values.code,
      });

      if (response.code === 0) {
        message.success('手机号更新成功');

        // 手机号更新成功后，获取最新的用户信息（强制刷新缓存）
        try {
          // 由于手机号刚更新，需要强制刷新缓存获取最新信息
          const profileResponse = await authService.getUserProfile(true);
          if (profileResponse.code === 0 && profileResponse.data) {
            console.log('[PhoneLoginModal] 获取最新用户信息成功:', profileResponse.data);

            // 调用成功回调，传递最新的用户信息
            if (_onSuccess) {
              _onSuccess({
                user_id: profileResponse.data.user_id,
                user_name: profileResponse.data.user_name,
                user_avatar: profileResponse.data.user_avatar,
                mobile: profileResponse.data.mobile,
              });
            }
          } else {
            console.warn('[PhoneLoginModal] 获取用户信息失败，使用输入的手机号');
            // 如果获取用户信息失败，仍然使用输入的手机号
            if (_onSuccess) {
              _onSuccess({
                mobile: values.phone,
              });
            }
          }
        } catch (profileError) {
          console.error('[PhoneLoginModal] 获取用户信息异常:', profileError);
          // 如果获取用户信息异常，仍然使用输入的手机号
          if (_onSuccess) {
            _onSuccess({
              mobile: values.phone,
            });
          }
        }
      } else {
        // 使用统一的错误处理
        const errorInfo = handleSmsError(response, '手机号更新');
        message.error(getUserFriendlyMessage(errorInfo));
      }
    } catch (error: any) {
      console.error('手机号更新失败:', error);
      // 使用统一的短信错误处理
      const errorInfo = handleSmsError(error, '手机号更新');
      message.error(getUserFriendlyMessage(errorInfo));
    } finally {
      setLoading(false);
    }
  };

  // 处理关闭按钮点击
  const handleCancel = () => {
    // 显示提示信息
    // message.warning('首次创作前，请先完成手机号验证');
    message.warning('完成手机号验证，可以免费领取25米粒值');
    console.log('[PhoneLoginModal] 用户关闭手机号验证弹窗');
    // 调用父组件的onCancel回调
    if (_onCancel) {
      _onCancel();
    }
  };

  return (
    <StyledModal
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      maskClosable={false}
      closable={false}
    >
      <ModalHeader>
        <ModalTitle>
          完成手机号验证，可以免费领取25米粒值
          {/* 完成手机号验证，可以免费领取 <span style={{ color: '#FF6D49' }}>25米粒值</span> */}
        </ModalTitle>
        <CloseButton onClick={handleCancel}>
          <img src={ErrorIcon} alt="Close" />
        </CloseButton>
      </ModalHeader>

      <LoginForm
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ countryCode: '+86' }}
        onValuesChange={handleFormValuesChange}
      >
        {/* 手机号输入框 */}
        <Form.Item
          name="phone"
          rules={[
            { required: true, message: '请输入手机号' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const phoneRegex = /^1[3-9]\d{9}$/;
                if (!phoneRegex.test(value)) {
                  return Promise.reject(new Error('请输入正确的手机号'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <PhoneInputContainer>
            <PhonePrefix>+86</PhonePrefix>
            <PhoneDivider />
            <PhoneInput placeholder="请输入手机号" />
          </PhoneInputContainer>
        </Form.Item>

        {/* 验证码输入框 */}
        <Form.Item
          name="code"
          rules={[
            { required: true, message: '请输入验证码' },
            {
              pattern: /^\d{6}$/,
              message: '请输入正确的验证码',
            },
          ]}
        >
          <VerificationContainer>
            <VerificationInput placeholder="请输入验证码" />
            <SendCodeButton
              type="primary"
              className={countdown > 0 ? 'countdown' : ''}
              onClick={handleSendCode}
              disabled={!isPhoneValid || sendingCode || countdown > 0}
            >
              {countdown > 0 ? `${countdown} s` : '发送验证码'}
            </SendCodeButton>
          </VerificationContainer>
        </Form.Item>

        {/* 协议说明 */}
        <AgreementText>
          验证通过即代表同意{' '}
          <AgreementLink href="/agreement/privacy-policy" target="_blank">
            用户协议及隐私政策
          </AgreementLink>
        </AgreementText>

        {/* 提交按钮 */}
        <Form.Item style={{ textAlign: 'center', marginBottom: 0, marginTop: 10 }}>
          <SubmitButton
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={isSubmitDisabled || loading}
          >
            立即创作
            <ArrowIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="13"
                viewBox="0 0 14 13"
                fill="none"
              >
                <path
                  d="M13.9302 6.26784C13.9022 6.10238 13.8112 5.94329 13.6712 5.83511L8.33719 0.986021C8.01519 0.737839 7.53219 0.769657 7.25219 1.06238C7.00719 1.32329 7.00719 1.71148 7.25219 1.97238L11.3192 5.66966H0.875192C0.448192 5.66966 0.105192 5.98148 0.105192 6.36966C0.105192 6.75784 0.448192 7.06966 0.875192 7.06966H11.3192L7.25219 10.7669C6.95119 11.0406 6.95119 11.4797 7.24519 11.7533C7.54619 12.0269 8.02919 12.0269 8.33019 11.7597L13.7202 6.86602C13.8882 6.70693 13.9652 6.4842 13.9302 6.26784Z"
                  fill="white"
                />
              </svg>
            </ArrowIcon>
          </SubmitButton>
        </Form.Item>
      </LoginForm>
    </StyledModal>
  );
};

export default PhoneLoginModal;
